import { centsToEuros } from '@/constants'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import type { InventoryItemData } from '@jobilla/nexus-sdk'
import { MinusIcon, PlusIcon, SparkleIcon, XIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../rapid-ui'
import { allocateInventory, inventoryItemName, type InventorySelection } from './inventory-allocation'

type InventoryModalProps = {
	open: boolean
	items: InventoryItemData[]
	selection: InventorySelection
	campaignCostCents: number
	currencyFormatter: Intl.NumberFormat
	onClose: () => void
	onApply: (selection: InventorySelection) => void
}

export function InventoryModal({ open, onClose, ...contentProps }: InventoryModalProps) {
	return (
		<Dialog open={open} onClose={onClose} className="relative z-[60]">
			<DialogBackdrop
				transition={true}
				className="fixed inset-0 bg-black/30 transition-opacity duration-200 ease-out data-closed:opacity-0"
			/>

			<div className="fixed inset-0 flex items-center justify-center p-4">
				<DialogPanel
					transition={true}
					className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col text-sm transition duration-200 ease-out data-closed:scale-95 data-closed:opacity-0"
				>
					<InventoryModalContent onClose={onClose} {...contentProps} />
				</DialogPanel>
			</div>
		</Dialog>
	)
}

type InventoryModalContentProps = Omit<InventoryModalProps, 'open'>

function InventoryModalContent(
	{ items, selection, campaignCostCents, currencyFormatter, onClose, onApply }: InventoryModalContentProps,
) {
	const { t, i18n } = useTranslation('editor')
	const [draft, setDraft] = useState<InventorySelection>(selection)

	const allocation = useMemo(
		() => allocateInventory(items, draft, campaignCostCents),
		[items, draft, campaignCostCents],
	)
	const formatCents = (cents: number) => currencyFormatter.format(centsToEuros(cents))
	const isOverBudget = allocation.topUpCents > 0
	const fillPercent = campaignCostCents > 0
		? Math.min(100, (allocation.coveredCents / campaignCostCents) * 100)
		: 0

	const changeQuantity = (item: InventoryItemData, delta: number) => {
		setDraft((current) => {
			const quantity = Math.max(0, Math.min(item.availableQuantity, (current[item.id] ?? 0) + delta))
			const { [item.id]: _removed, ...rest } = current
			return quantity > 0 ? { ...rest, [item.id]: quantity } : rest
		})
	}

	return (
		<>
			<div className="flex items-start gap-4 p-6 pb-4">
				<div className="flex-1">
					<DialogTitle className="text-lg font-semibold text-grey-900">
						{t('billingDrawer.inventoryModal.title')}
					</DialogTitle>
					<p className="mt-1 text-grey-700">
						{t('billingDrawer.inventoryModal.description')}
					</p>
				</div>
				<button
					type="button"
					aria-label={t('billingDrawer.inventoryModal.close')}
					onClick={onClose}
					className="size-9 flex items-center justify-center rounded-lg text-grey-500 hover:bg-grey-50 cursor-pointer"
				>
					<XIcon className="size-5" />
				</button>
			</div>

			<div
				className={`mx-6 mb-2 p-4 rounded-lg flex flex-col gap-2.5 ${
					isOverBudget ? 'bg-brand-50' : 'bg-grey-50'
				}`}
			>
				<div className="flex items-baseline justify-between gap-3">
					<span className="font-semibold text-grey-900">
						{t('billingDrawer.inventoryModal.campaignCost')}
					</span>
					<span className="text-lg font-bold text-grey-900 tabular-nums">
						{formatCents(campaignCostCents)}
					</span>
				</div>
				<div
					role="img"
					aria-label={t('billingDrawer.inventoryModal.costBarLabel', {
						covered: formatCents(allocation.coveredCents),
						cost: formatCents(campaignCostCents),
					})}
					className="h-2 rounded-full overflow-hidden bg-grey-100"
				>
					<div
						className="relative h-full overflow-hidden rounded-full bg-gradient-to-r from-brand-700 to-brand-500 transition-[width] duration-300"
						style={{ width: `${fillPercent}%` }}
					>
						{isOverBudget
							? (
								<div className="shimmer-sweep absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent" />
							)
							: null}
					</div>
				</div>
				<div className="flex items-center justify-between gap-3 text-[13px]">
					<span>
						<span className="font-semibold text-brand-700 tabular-nums">
							{formatCents(allocation.coveredCents)}
						</span>{' '}
						<span className="text-grey-700">
							{t('billingDrawer.inventoryModal.fromInventory')}
						</span>
					</span>
					{isOverBudget
						? (
							<span className="relative inline-flex items-center gap-1 pr-1.5 font-semibold text-brand-800">
								<SparkleIcon
									aria-hidden="true"
									className="sparkle-twinkle absolute -left-4 -top-2.5 size-3 fill-brand-500 text-brand-500"
								/>
								<SparkleIcon
									aria-hidden="true"
									className="sparkle-twinkle absolute -right-2 -top-3 size-2.5 fill-brand-300 text-brand-300 [animation-delay:600ms]"
								/>
								<SparkleIcon
									aria-hidden="true"
									className="sparkle-twinkle absolute right-6 -bottom-3 size-2 fill-brand-700 text-brand-700 [animation-delay:1200ms]"
								/>
								<span className="tabular-nums">
									+{formatCents(allocation.topUpCents)}
								</span>
								<span className="font-medium">
									{t('billingDrawer.walletTopUp')}
								</span>
							</span>
						)
						: (
							<span>
								<span className="font-semibold text-grey-900 tabular-nums">
									{formatCents(allocation.remainingCents)}
								</span>{' '}
								<span className="text-grey-700">
									{t('billingDrawer.inventoryModal.remaining')}
								</span>
							</span>
						)}
				</div>
			</div>

			<ul className="px-6 overflow-y-auto divide-y divide-grey-100">
				{items.map((item) => {
					const quantity = draft[item.id] ?? 0
					const name = inventoryItemName(item, t)
					return (
						<li key={item.id} className="flex items-center gap-3 py-3">
							<div className="flex-1 min-w-0 flex flex-col leading-snug">
								<span className="text-[15px] font-semibold text-grey-900 truncate">
									{name}
								</span>
								<span className="text-xs text-grey-700 tabular-nums">
									{t('billingDrawer.inventoryModal.itemPrice', {
										price: formatCents(item.valueCents),
										available: item.availableQuantity,
									})}
								</span>
								<span className="text-xs text-grey-700">
									{t('billingDrawer.inventoryModal.expires', {
										date: new Date(item.expiresAt).toLocaleDateString(i18n.language),
									})}
								</span>
							</div>
							<div className="flex items-center gap-2">
								<button
									type="button"
									aria-label={t('billingDrawer.inventoryModal.decrease', { name })}
									disabled={quantity === 0}
									onClick={() => changeQuantity(item, -1)}
									className="size-8 flex items-center justify-center rounded-md border border-grey-200 bg-white text-grey-900 cursor-pointer disabled:cursor-not-allowed disabled:bg-grey-50 disabled:text-grey-300"
								>
									<MinusIcon className="size-3.5" />
								</button>
								<span className="min-w-5 text-center font-semibold text-grey-900 tabular-nums">
									{quantity}
								</span>
								<button
									type="button"
									aria-label={t('billingDrawer.inventoryModal.increase', { name })}
									disabled={quantity >= item.availableQuantity}
									onClick={() => changeQuantity(item, 1)}
									className="size-8 flex items-center justify-center rounded-md border border-grey-200 bg-white text-grey-900 cursor-pointer disabled:cursor-not-allowed disabled:bg-grey-50 disabled:text-grey-300"
								>
									<PlusIcon className="size-3.5" />
								</button>
							</div>
							<span
								className={`w-24 text-right tabular-nums ${
									quantity > 0 ? 'font-semibold text-grey-900' : 'text-grey-500'
								}`}
							>
								{quantity > 0 ? formatCents(item.valueCents * quantity) : '—'}
							</span>
						</li>
					)
				})}
			</ul>

			<div className="mt-5 border-t border-grey-100 px-6 py-4 flex justify-end gap-2">
				<Button type="neutral" onClick={onClose}>
					{t('billingDrawer.inventoryModal.cancel')}
				</Button>
				<Button type="green" onClick={() => onApply(draft)}>
					{allocation.appliedCents > 0
						? t('billingDrawer.inventoryModal.applyAmount', {
							amount: formatCents(allocation.appliedCents),
						})
						: t('billingDrawer.inventoryModal.apply')}
				</Button>
			</div>
		</>
	)
}
